import { useContext, useEffect, useState } from 'react'

import { LoadingOverlay, Box, Menu, Tooltip, ActionIcon } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'

import { navigate, useLocation } from '@redwoodjs/router'
import { useQuery, MetaTags, useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { SearchBox } from 'src/components/Inputs/SearchBox/SearchBox'
import PaginationArea from 'src/components/Navigation/PaginationArea/PaginationArea'
import FilterMenu from 'src/components/Overlays/FiltersMenu/FilterMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import { RecruitersContext } from 'src/context/RecruitersContext'
import {
  GET_JOB_LISTINGS,
  UPDATE_JOB_LISTING,
} from 'src/graphql/joblistings.graphql'
import { JobStatus } from 'src/graphql/types/jobListings'
import { usePage } from 'src/hooks/usePage/usePage'
import IconAdd from 'src/icons/IconAdd'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconClose from 'src/icons/IconClose'
import { buildCompanyApplicationUrl, buildJobBoardUrl } from 'src/lib/url.utils'

import BulkJobListingEmployeeAssignmentDrawer from './components/BulkJobListingEmployeeAssignmentDrawer/BulkJobListingEmployeeAssignmentDrawer'
import CreateJobListingModal from './components/CreateJobListingModal/CreateJobListingModal'
import JobListingDetailsDrawer from './components/JobListingDetailsDrawer'
import JobsListingTable, {
  type SelectedJobListing,
} from './components/JobsListingTable/JobsListingTable'

const JobsPage = () => {
  const currentCompany = useCurrentCompanyContext()
  const [opened, { open, close }] = useDisclosure(false)
  const [bulkAssignmentDrawerOpened, bulkAssignmentDrawerHandlers] =
    useDisclosure(false)
  const recruiters = useContext(RecruitersContext)
  const { search } = useLocation()
  const [page, setPage] = usePage()
  const [selectedJobListings, setSelectedJobListings] = useState<
    SelectedJobListing[]
  >([])

  // Initialize filters from URL parameters
  const getInitialFilters = () => {
    const params = new URLSearchParams(search)

    // Get employee filters from URL (supports multiple employees params)
    const employeeIds = params.getAll('employees').filter(Boolean)

    // Get status filters from URL (supports multiple statuses params)
    const statusParams = params.getAll('statuses').filter(Boolean)
    const statuses = statusParams.length > 0
      ? statusParams
      : [JobStatus.ACTIVE, JobStatus.INACTIVE]

    // Get search from URL
    const searchParam = params.get('search') || ''

    return { employeeIds, statuses, searchParam }
  }

  const initialFilters = getInitialFilters()

  const [selectedRecruiters, setSelectedRecruiters] = useState([])
  const [selectedStatuses, setSelectedStatuses] = useState(initialFilters.statuses)
  const [searchValue, setSearchValue] = useState(initialFilters.searchParam)
  const [searchQuery, setSearchQuery] = useState(initialFilters.searchParam)
  const [selectedJobListingId, setSelectedJobListingId] = useState<
    string | null
  >(null)
  const [openedDetails, { open: openDetails, close: closeDetails }] =
    useDisclosure(false)

  // Get initial page size from URL or default to 20
  const getInitialPageSize = () => {
    const params = new URLSearchParams(search)
    const size = parseInt(params.get('pageSize'), 10)
    return [10, 20, 50, 100].includes(size) ? size : 20
  }

  const [pageSize, setPageSize] = useState(getInitialPageSize)

  /**********************    QUERIES    **********************/

  const { data, loading, refetch } = useQuery(GET_JOB_LISTINGS, {
    variables: {
      pageInfo: { offset: (page - 1) * pageSize, limit: pageSize },
      filters: {
        recruiters: selectedRecruiters.map((recruiter) => recruiter.value),
        statuses:
          selectedStatuses.length > 0
            ? selectedStatuses
            : [JobStatus.ACTIVE, JobStatus.INACTIVE],
        search: searchQuery || undefined,
      },
    },
    notifyOnNetworkStatusChange: false,
  })
  const jobsData = data?.jobListings?.items ?? []

  const [updateJobListing] = useMutation(UPDATE_JOB_LISTING, {
    onCompleted: () => {
      toast('Job listing status updated successfully', 'success')
      refetch()
    },
    onError: (error) => {
      console.error('Error updating job listing status:', error)
      toast('Unable to update job listing status, please try again.', 'error')
    },
  })

  /**********************    FUNCTIONS    **********************/

  const handleSearch = (value: string) => {
    setSearchQuery(value)
    setPage(1) // Reset to page 1 when searching
    updateURLFilters({ search: value })
  }

  const handlePageSizeChange = (newSize: number) => {
    const params = new URLSearchParams(window.location.search)
    params.set('pageSize', newSize.toString())
    params.set('page', '1') // Reset to page 1 when changing page size
    navigate(`?${params.toString()}`)
    setPageSize(newSize)
  }

  // Initialize selectedRecruiters from URL once recruiters are loaded
  useEffect(() => {
    if (recruiters && recruiters.length > 0 && initialFilters.employeeIds.length > 0) {
      const selected = recruiters.filter((r) =>
        initialFilters.employeeIds.includes(r.value)
      )
      if (selected.length > 0) {
        setSelectedRecruiters(selected)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recruiters])

  // Function to update URL with current filters
  const updateURLFilters = (filters: {
    employees?: string[]
    statuses?: string[]
    search?: string
  }) => {
    const params = new URLSearchParams(window.location.search)

    // Update employees filter (using multiple params with same key)
    if (filters.employees !== undefined) {
      params.delete('employees') // Remove all existing employees params
      filters.employees.forEach(employeeId => {
        params.append('employees', employeeId)
      })
    }

    // Update statuses filter (using multiple params with same key)
    if (filters.statuses !== undefined) {
      const defaultStatuses = [JobStatus.ACTIVE, JobStatus.INACTIVE]
      const isDefault =
        filters.statuses.length === defaultStatuses.length &&
        filters.statuses.every(s => defaultStatuses.includes(s))

      params.delete('statuses') // Remove all existing statuses params
      if (filters.statuses.length > 0 && !isDefault) {
        filters.statuses.forEach(status => {
          params.append('statuses', status)
        })
      }
    }

    // Update search filter
    if (filters.search !== undefined) {
      if (filters.search) {
        params.set('search', filters.search)
      } else {
        params.delete('search')
      }
    }

    navigate(`?${params.toString()}`)
  }

  // Sync pageSize from URL changes
  useEffect(() => {
    const params = new URLSearchParams(search)
    const size = parseInt(params.get('pageSize'), 10)
    if ([10, 20, 50, 100].includes(size) && size !== pageSize) {
      setPageSize(size)
    }
  }, [search])

  // Only reset to page 1 when recruiter filters actually change (not on initial mount)
  useEffect(() => {
    if (selectedRecruiters.length > 0) {
      setPage(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecruiters.length])

  // Reset to page 1 when status filters change
  useEffect(() => {
    if (selectedStatuses.length > 0) {
      setPage(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatuses.length])

  const handleSelectedJobListing = (jobListingId: string | null) => {
    if (jobListingId) {
      setSelectedJobListingId(jobListingId)
      openDetails()
    }
  }

  const handleDrawerClose = () => {
    closeDetails()
    setSelectedJobListingId(null)
    // Don't refetch here - mutations in drawer will handle cache updates via refetchQueries
  }

  const handleStatusChange = (jobListing, isActive) => {
    updateJobListing({
      variables: {
        id: jobListing.jobListingId,
        input: {
          status: isActive ? JobStatus.ACTIVE : JobStatus.INACTIVE,
        },
      },
    })
  }

  return (
    <div className="flex h-full w-full flex-col gap-5">
      {opened && (
        <CreateJobListingModal
          isModalOpen={opened}
          onModalClose={close}
          recruiters={recruiters}
        />
      )}

      {openedDetails && selectedJobListingId && (
        <JobListingDetailsDrawer
          onClose={handleDrawerClose}
          jobListingId={selectedJobListingId}
          onJobListingUpdate={refetch}
        />
      )}

      <BulkJobListingEmployeeAssignmentDrawer
        opened={bulkAssignmentDrawerOpened}
        onClose={bulkAssignmentDrawerHandlers.close}
        selectedJobListings={selectedJobListings}
        setSelectedJobListings={setSelectedJobListings}
      />

      <MetaTags title="Jobs" description="Jobs page" />

      <div className="flex h-full flex-col gap-3 px-5 pb-3">
        <div className="flex flex-col gap-2 ">
          <div className="flex flex-1  flex-row items-center">
            <div className="flex flex-col">
              <div className="text-lg font-semibold text-doubleNickel-gray-900">
                Job Listings
              </div>
              <div className="text-sm text-doubleNickel-gray-600">
                {/* These spans are necessary to prevent Google Translate issues  */}
                <span>{data?.jobListings.totalCount}</span>
                <span> job listings</span>
              </div>
            </div>

            <div className="flex flex-1 flex-row items-center justify-end gap-4">
              <div className="w-80">
                <SearchBox
                  value={searchValue}
                  setValue={setSearchValue}
                  placeholder="Search job listings by name..."
                  onSearch={handleSearch}
                />
              </div>
              <FilterMenu
                filters={[
                  {
                    title: 'Employees',
                    selected: selectedRecruiters.map((r) => r.value),
                    onChange: (values) => {
                      const selected = recruiters.filter((r) =>
                        values.includes(r.value)
                      )
                      setSelectedRecruiters(selected)
                      updateURLFilters({ employees: values })
                    },
                    options: recruiters || [],
                  },
                  {
                    title: 'Status',
                    selected: selectedStatuses,
                    onChange: (values) => {
                      // If empty, default to showing both statuses
                      const newStatuses = values.length > 0
                        ? values
                        : [JobStatus.ACTIVE, JobStatus.INACTIVE]
                      setSelectedStatuses(newStatuses)
                      updateURLFilters({ statuses: newStatuses })
                    },
                    options: [
                      { value: JobStatus.ACTIVE, label: 'Active' },
                      { value: JobStatus.INACTIVE, label: 'Draft' },
                    ],
                  },
                ]}
              />
              <Menu
                position="bottom-end"
                offset={3}
                withArrow
                shadow="md"
                width={200}
              >
                <Menu.Target>
                  <DNButton type="button">
                    <IconChevronDown className="h-4 w-4 fill-none stroke-doubleNickel-gray-700" />
                    Actions
                  </DNButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Tooltip
                    label="Please select job listings first"
                    disabled={selectedJobListings.length > 0}
                    position="left"
                  >
                    <Menu.Item
                      className="text-sm"
                      onClick={bulkAssignmentDrawerHandlers.open}
                      disabled={selectedJobListings.length === 0}
                    >
                      Assign Employees
                      {selectedJobListings.length > 0 && (
                        <span className="ml-1 text-doubleNickel-gray-500">
                          ({selectedJobListings.length})
                        </span>
                      )}
                    </Menu.Item>
                  </Tooltip>
                  <Tooltip
                    label="No company link set up yet"
                    disabled={!!currentCompany?.companySlug}
                    position="left"
                  >
                    <Menu.Item
                      className="text-sm"
                      disabled={!currentCompany?.companySlug}
                      onClick={() => {
                        navigator.clipboard.writeText(
                          buildCompanyApplicationUrl(currentCompany.companySlug)
                        )
                        toast('Copied to clipboard', 'success')
                      }}
                    >
                      Copy Full App Link
                    </Menu.Item>
                  </Tooltip>
                  <Menu.Item
                    className="text-sm"
                    disabled={!currentCompany?.companyId}
                    onClick={() => {
                      const url = buildJobBoardUrl(
                        currentCompany?.companyId ?? ''
                      )
                      navigator.clipboard.writeText(url)
                      toast('Copied to clipboard', 'success')
                    }}
                  >
                    Copy Job Board Link
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              <DNButton onClick={open} variant="primary">
                <IconAdd className="h-4 fill-none stroke-doubleNickel-white" />
                Create Job Listing
              </DNButton>
            </div>
          </div>
        </div>

        <div className="flex flex-row items-center gap-4 text-sm font-semibold text-doubleNickel-brand-500">
          {selectedJobListings.length > 0 && (
            <div className="flex flex-row items-center gap-1">
              <div className="text-nowrap">
                {selectedJobListings.length}{' '}
                {selectedJobListings.length === 1
                  ? 'job listing'
                  : 'job listings'}{' '}
                selected
              </div>
              <Tooltip label="Clear all selected job listings">
                <ActionIcon
                  variant="subtle"
                  onClick={() => setSelectedJobListings([])}
                >
                  <IconClose className="fill-none stroke-doubleNickel-brand-500" />
                </ActionIcon>
              </Tooltip>
            </div>
          )}
        </div>

        <div className="flex h-full flex-col gap-2 overflow-hidden">
          <div className={`flex h-full w-full flex-1 flex-col`}>
            <Box
              className="flex h-full flex-col overflow-auto"
              pos={'relative'}
            >
              <JobsListingTable
                jobsData={jobsData}
                loading={loading}
                setSelectedJobListing={handleSelectedJobListing}
                activeRecruiters={recruiters}
                onStatusChange={handleStatusChange}
                selectedJobListings={selectedJobListings}
                setSelectedJobListings={setSelectedJobListings}
              />
              <LoadingOverlay
                visible={loading}
                overlayProps={{ opacity: 0 }}
                zIndex={1000}
              />
            </Box>
            <PaginationArea
              page={page}
              totalPages={Math.ceil(data?.jobListings.totalCount / pageSize)}
              onPageChange={setPage}
              resultsPerPage={pageSize}
              setResultsPerPage={handlePageSizeChange}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default JobsPage
